import React from 'react';
import Icon from './Icon';

export const Btn = ({ children, variant = 'primary', icon, onClick, type = 'button', disabled, style, title }) => {
  const variants = {
    primary:   { bg: 'var(--red)',          color: '#fff',           border: 'transparent' },
    secondary: { bg: 'var(--surface2)',     color: 'var(--text)',    border: 'var(--border)' },
    ghost:     { bg: 'transparent',         color: 'var(--text-mute)', border: 'var(--border)' },
    danger:    { bg: 'transparent',         color: 'var(--red)',     border: 'var(--red)' },
    success:   { bg: 'var(--green-deep)',   color: '#fff',           border: 'transparent' },
  };
  const v = variants[variant] || variants.primary;
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      title={title}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        padding: '10px 16px',
        borderRadius: 10,
        background: v.bg,
        color: v.color,
        border: `1px solid ${v.border}`,
        fontSize: 14,
        fontWeight: 600,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        transition: 'all 0.15s',
        whiteSpace: 'nowrap',
        ...(style || {}),
      }}
    >
      {icon && <Icon name={icon} size={18} />}
      {children}
    </button>
  );
};

export const Card = ({ children, style, className }) => (
  <div
    className={className}
    style={{
      background: 'var(--surface)',
      borderRadius: 16,
      border: '1px solid var(--border)',
      ...(style || {}),
    }}
  >
    {children}
  </div>
);

export const Label = ({ children }) => (
  <div
    style={{
      fontSize: 11,
      fontFamily: 'var(--font-mono)',
      letterSpacing: '0.1em',
      textTransform: 'uppercase',
      color: 'var(--text-mute)',
      marginBottom: 8,
    }}
  >
    {children}
  </div>
);

const inputStyle = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '12px 14px',
  borderRadius: 10,
  background: 'var(--surface)',
  color: 'var(--text)',
  border: '1px solid var(--border)',
  fontSize: 15,
  fontWeight: 500,
  outline: 'none',
  transition: 'border-color 0.15s',
};

export const TextInput = ({ value, onChange, placeholder, type = 'text', ...rest }) => (
  <input
    type={type}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    style={inputStyle}
    onFocus={(e) => (e.target.style.borderColor = 'var(--red)')}
    onBlur={(e) => (e.target.style.borderColor = 'var(--border)')}
    {...rest}
  />
);

export const SelectInput = ({ value, onChange, options }) => (
  <select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    style={{ ...inputStyle, cursor: 'pointer' }}
    onFocus={(e) => (e.target.style.borderColor = 'var(--red)')}
    onBlur={(e) => (e.target.style.borderColor = 'var(--border)')}
  >
    {options.map((o) => (
      <option key={o.value} value={o.value}>{o.label}</option>
    ))}
  </select>
);

export const TextArea = ({ value, onChange, placeholder, rows = 3 }) => (
  <textarea
    value={value}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    rows={rows}
    style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.5, fontWeight: 400 }}
    onFocus={(e) => (e.target.style.borderColor = 'var(--red)')}
    onBlur={(e) => (e.target.style.borderColor = 'var(--border)')}
  />
);

export const Section = ({ title, subtitle, action, children }) => (
  <section>
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'space-between',
        marginBottom: 20,
        gap: 16,
      }}
    >
      <div>
        <h2
          style={{
            fontSize: 24,
            fontWeight: 700,
            color: 'var(--text)',
            margin: 0,
            letterSpacing: '-0.01em',
          }}
        >
          {title}
        </h2>
        {subtitle && (
          <div style={{ fontSize: 14, color: 'var(--text-mute)', marginTop: 4 }}>{subtitle}</div>
        )}
      </div>
      {action}
    </div>
    {children}
  </section>
);

export const StatusPill = ({ status }) => {
  const map = {
    erstellt: { color: 'var(--amber)',    bg: 'rgba(220,160,40,0.12)',  label: 'Erstellt', icon: 'pending' },
    aktiv:    { color: 'var(--red)',      bg: 'rgba(220,40,40,0.15)',   label: 'Aktiv',    icon: 'priority_high' },
    beendet:  { color: 'var(--text-dim)', bg: 'rgba(255,255,255,0.05)', label: 'Beendet',  icon: 'check_circle' },
  };
  const s = map[status] || map.erstellt;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '4px 10px',
        borderRadius: 999,
        background: s.bg,
        color: s.color,
        fontSize: 12,
        fontWeight: 600,
        letterSpacing: '0.04em',
        fontFamily: 'var(--font-mono)',
        textTransform: 'uppercase',
      }}
    >
      <Icon name={s.icon} size={14} />
      {s.label}
    </span>
  );
};

// Hooks
export const useNow = (intervalMs = 1000) => {
  const [now, setNow] = React.useState(() => new Date());
  React.useEffect(() => {
    const id = setInterval(() => setNow(new Date()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
};

export const elapsedSince = (iso, now) => {
  if (!iso) return '00:00';
  const ms = Math.max(0, now.getTime() - new Date(iso).getTime());
  const total = Math.floor(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n) => String(n).padStart(2, '0');
  return h > 0 ? `${pad(h)}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
};

export const fmtTime = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
};

export const fmtDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
};
